'use client';

import { useEffect, useRef, useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import { NextImageProps } from '@/decodo/components/atoms/Image';
import LinkButton, {
  LinkButtonProps,
} from '@/decodo/components/atoms/LinkButton';
import { getWrapperMaxWidth } from '@/features/utils/utils';

import { AnimatedBackground } from './components/AnimatedBackground';
import { Features } from './components/Features';
import {
  HomepageCarousel,
  HomepageCarouselProps,
} from './components/HomepageCarousel';
import { MoneyBack } from './components/MoneyBack';
import { ReputationBadges } from './components/ReputationBadges';
import { handleHeroButtonLinkClick } from './helpers';
import Heading from '@/decodo/components/atoms/Heading';

export type HomepageHeroProps = {
  title: string;
  description: React.ReactNode;
  buttons: LinkButtonProps[];
  showMoneyBack?: boolean;
  features: string[];
  featureIcon?: NextImageProps;
  carousel: HomepageCarouselProps['slides'];
};

export function HomepageHero({
  title,
  description,
  buttons,
  showMoneyBack,
  features,
  featureIcon,
  carousel,
}: HomepageHeroProps) {
  const { texts } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const stickyElRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const adjustedDelayForTitle = 0.25;

  useEffect(() => {
    if (stickyElRef.current) {
      setScrollTop(stickyElRef.current.getBoundingClientRect().top);
    }
  }, []);

  return (
    <section
      ref={containerRef}
      className='bg-gray-25 relative lg:p-10'
      data-tracker-section={SECTION_NAMES.HERO}
    >
      <div className='absolute inset-0 flex items-end justify-center overflow-hidden'>
        <AnimatedBackground />
      </div>
      <div
        className={cn(
          getWrapperMaxWidth('mid'),
          'relative mx-auto flex flex-col gap-y-8 rounded-lg  bg-gray-100 bg-opacity-[0.01] px-5 py-8 backdrop-blur-[6px]  md:gap-y-[60px] md:p-10'
        )}
      >
        <div
          style={{
            top: scrollTop || 96, // "top-24" === "96px"
          }}
          ref={stickyElRef}
          className='sticky top-24 flex origin-top flex-col gap-y-8 md:z-[-1]'
        >
          <div className=' flex flex-col justify-center gap-y-6 md:items-center'>
            <ReputationBadges delayFactor={adjustedDelayForTitle} />
            <Heading
              level='h1'
              classNames={{
                root: 'dark:text-base-black text-h1 md:text-center md:text-l overflow-hidden',
              }}
            >
              {title}
            </Heading>
            <div className='dark:text-base-black font-mono'>{description}</div>
          </div>
          <div className='flex flex-col items-start gap-y-4 md:items-center'>
            <div className='flex flex-col gap-x-6 gap-y-6 self-stretch md:flex-row md:self-auto'>
              {buttons.map((button) => (
                <div key={button.id} className={cn('w-full md:w-auto')}>
                  <LinkButton
                    {...button}
                    fullwidth
                    onClick={(
                      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                    ) => handleHeroButtonLinkClick(e, button.href)}
                  />
                </div>
              ))}
            </div>
            {showMoneyBack && (
              <div key='money-back'>
                <MoneyBack
                  text={texts.pricing_cards.money_back.money_back_option}
                />
              </div>
            )}
          </div>
          {features.length > 0 && (
            <Features
              features={features}
              icon={featureIcon}
              delayFactor={adjustedDelayForTitle}
            />
          )}
        </div>
        {carousel.length > 0 && <HomepageCarousel slides={carousel} />}
      </div>
    </section>
  );
}
